import { AlertTriangle, ExternalLink, Globe, Lock, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteCard, { StatusPill } from '../../components/admin/SiteCard';
import {
  billingLabelForSite,
  daysUntil,
  formatDate,
  siteDomain,
  siteName,
} from '../../components/admin/adminUtils';
import axiosInstance from '../../helpers/axiosConfig';
import { apiErrorCode, apiStatus, authHeaders, parseApiError } from '../../helpers/billingApi';
import { openWpAdmin } from '../../helpers/provisioningApi';
import ConfirmSubscribeModal from '../../components/admin/ConfirmSubscribeModal';

export default function SiteDetailPage() {
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const [wpCloud, setWpCloud] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingDomain, setSavingDomain] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dnsConfig, setDnsConfig] = useState(null);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [wpLoginBusy, setWpLoginBusy] = useState(false);
  const [loadingDns, setLoadingDns] = useState(false);

  const loadSite = async () => {
    setError('');
    try {
      const response = await axiosInstance.get(`site-management/${id}`, authHeaders());
      setSite(response.data.site);
      setWpCloud(response.data.wp_cloud);
      // Keep whatever the customer has typed if the site has no domain yet —
      // reloading mid-flow (e.g. right after subscribing) must not wipe it.
      setCustomDomain((current) => response.data.site?.custom_domain || current);
    } catch (requestError) {
      setError(parseApiError(requestError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get(`site-management/${id}`, authHeaders());
        if (!isMounted) return;
        setSite(response.data.site);
        setWpCloud(response.data.wp_cloud);
        setCustomDomain(response.data.site?.custom_domain || '');
      } catch (requestError) {
        if (!isMounted) return;
        setError(parseApiError(requestError));
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Applying the domain is its own function so the subscribe flow can finish
  // the job the customer already asked for, instead of making them retype the
  // domain and press the button a second time.
  const applyDomain = async (domain) => {
    if (!domain) return;
    setSavingDomain(true);
    setMessage('');
    setError('');
    setDnsConfig(null);
    setSubscriptionRequired(false);
    try {
      const response = await axiosInstance.put(
        `site-management/${id}/domain`,
        { domain },
        authHeaders(),
      );
      setSite((current) => ({ ...current, custom_domain: response.data.domain }));
      setDnsConfig(response.data.dns_configuration || null);
      setMessage('Domain updated. Add the DNS records below at your registrar.');
    } catch (requestError) {
      // US-008: the domain is a paid feature. A 402 SUBSCRIPTION_REQUIRED means
      // the site has no active subscription — route to subscribe, not a generic
      // error.
      if (apiStatus(requestError) === 402 && apiErrorCode(requestError) === 'SUBSCRIPTION_REQUIRED') {
        setSubscriptionRequired(true);
      } else {
        setError(parseApiError(requestError));
      }
    } finally {
      setSavingDomain(false);
    }
  };

  const updateDomain = (event) => {
    event.preventDefault();
    applyDomain(customDomain);
  };

  // Looking the records up again later. Resolving them costs a WP Cloud call,
  // so it happens when the customer asks rather than on every page load.
  const fetchDns = async () => {
    setLoadingDns(true);
    setError('');
    try {
      const response = await axiosInstance.get(`site-management/${id}/dns`, authHeaders());
      setDnsConfig(response.data.dns_configuration || null);
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not load the DNS records.'));
    } finally {
      setLoadingDns(false);
    }
  };

  // US-007: one-click magic login into wp-admin (no password). 409 means the
  // site's provisioning has not finished — surfaced as a plain message.
  const handleWpLogin = async () => {
    setWpLoginBusy(true);
    setError('');
    try {
      await openWpAdmin(id);
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not open WordPress. Try again shortly.'));
    } finally {
      setWpLoginBusy(false);
    }
  };

  // Both the upsell ("Subscribe to use a custom domain") and the grace-period
  // restore CTA open a confirm-and-pay modal first. The modal loads the plan
  // catalog from the API and hands back the plan the user confirmed.
  const requestSubscribe = () => {
    setError('');
    setMessage('');
    setConfirmOpen(true);
  };

  // Creating a subscription for this site is what unlocks the domain / restores
  // a disabled site. Charged immediately — confirmed in the modal.
  const confirmSubscribe = async (choice) => {
    setSubscribing(true);
    setError('');
    setMessage('');
    try {
      await axiosInstance.post(
        'subscriptions/',
        { site_id: id, plan_name: choice.planKey, billing_interval: choice.interval },
        authHeaders(),
      );
      setSubscriptionRequired(false);
      setConfirmOpen(false);
      await loadSite();

      // The subscribe exists to unblock a domain the customer already entered,
      // so finish that job rather than making them submit it again. Runs after
      // the reload because loadSite clears `error` — going the other way round
      // would swallow a domain failure the customer needs to see.
      if (customDomain) {
        await applyDomain(customDomain);
      } else {
        setMessage('Subscription active. You can now set a custom domain.');
      }
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not start the subscription.'));
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) return <div className="loading-block">Loading site</div>;
  if (error && !site) return <div className="error-banner">{error}</div>;

  const isDisabled = site?.status === 'disabled';
  const daysLeft = isDisabled ? daysUntil(site?.delete_due_at) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{siteName(site)}</h1>
          <div className="page-subtitle">{siteDomain(site)}</div>
        </div>
        <div className="page-actions">
          <StatusPill status={site?.status} />
          <a className="btn btn-ghost" href={`https://${siteDomain(site)}`} target="_blank" rel="noreferrer">
            <ExternalLink size={15} />
            Open site
          </a>
          <button className="btn btn-primary" type="button" onClick={handleWpLogin} disabled={wpLoginBusy}>
            {wpLoginBusy ? <RefreshCw size={15} className="spin" /> : <Lock size={15} />}
            {wpLoginBusy ? 'Opening...' : 'Log in to WordPress'}
          </button>
        </div>
      </div>

      {/* US-006 / US-007: deletion countdown for a canceled (disabled) site. */}
      {isDisabled ? (
        <div className="grace-banner">
          <AlertTriangle size={18} />
          <div className="grace-banner-copy">
            {daysLeft !== null ? (
              <strong>{daysLeft} day{daysLeft === 1 ? '' : 's'} until this site is deleted</strong>
            ) : (
              <strong>This site is scheduled for deletion</strong>
            )}
            <span>
              Your site stays online
              {site?.delete_due_at ? ` until ${formatDate(site.delete_due_at)}` : ''}. Update your
              billing before then to keep it.
            </span>
          </div>
          <button className="btn btn-primary" type="button" onClick={requestSubscribe} disabled={subscribing}>
            {subscribing ? 'Restoring...' : 'Reactivate / Resubscribe'}
          </button>
        </div>
      ) : null}

      {message ? <div className="success-banner admin-page-error">{message}</div> : null}
      {error ? <div className="error-banner admin-page-error">{error}</div> : null}

      <div className="site-detail-layout">
        <section className="site-detail-main">
          <SiteCard site={site} />

          <section className="panel-card card-pad domain-panel">
            <div className="card-header">
              <div>
                <span>Publish</span>
                <h2>Custom domain</h2>
              </div>
              <Globe size={20} />
            </div>
            <p>Publishing points a custom domain to this site. A custom domain requires an active subscription.</p>

            {/* US-008: subscription-required upsell */}
            {subscriptionRequired ? (
              <div className="upsell-banner">
                <strong>Subscribe to use a custom domain</strong>
                <p>A custom domain is a paid feature. Start a subscription for this site, then set your domain.</p>
                <button className="btn btn-primary" type="button" onClick={requestSubscribe} disabled={subscribing}>
                  {subscribing ? 'Starting...' : 'Subscribe to publish'}
                </button>
              </div>
            ) : null}

            <form className="domain-form" onSubmit={updateDomain}>
              <input
                type="text"
                placeholder="example.com"
                value={customDomain}
                onChange={(event) => setCustomDomain(event.target.value)}
              />
              <button className="btn btn-primary" type="submit" disabled={savingDomain || !customDomain}>
                {savingDomain ? <RefreshCw size={15} className="spin" /> : <Globe size={15} />}
                {savingDomain ? 'Updating...' : 'Update domain'}
              </button>
              {site?.custom_domain ? (
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={fetchDns}
                  disabled={loadingDns || savingDomain}
                >
                  <RefreshCw size={15} className={loadingDns ? 'spin' : undefined} />
                  {loadingDns ? 'Loading...' : 'Get current DNS'}
                </button>
              ) : null}
            </form>

            {/* US-008: DNS records to copy-paste on success */}
            {dnsConfig ? (
              <div className="dns-config">
                <div className="dns-callout">
                  <AlertTriangle size={16} />
                  <div>
                    <strong>Add these records to your DNS settings</strong>
                    <span>{dnsConfig.instructions}</span>
                  </div>
                </div>
                <div className="dns-records">
                  {(dnsConfig.records_needed || []).map((record) => (
                    <div className="dns-record" key={`${record.type}-${record.name}`}>
                      <span className="dns-type">{record.type}</span>
                      <span className="dns-name">{record.name}</span>
                      <span className="dns-value">
                        {Array.isArray(record.value) ? record.value.join(', ') : record.value}
                      </span>
                    </div>
                  ))}
                </div>
                {dnsConfig.ttl_recommended ? (
                  <small>Recommended TTL: {dnsConfig.ttl_recommended}s</small>
                ) : null}
                <ul className="dns-notes">
                  {(dnsConfig.notes || []).map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        </section>

        <aside className="site-detail-side">
          <section className="panel-card card-pad">
            <h2>Site information</h2>
            <dl className="detail-list">
              <div><dt>Status</dt><dd>{site?.status || 'Pending'}</dd></div>
              <div><dt>Billing</dt><dd>{billingLabelForSite(site)}</dd></div>
              <div><dt>Data center</dt><dd>{site?.data_center || 'dfw'}</dd></div>
              <div><dt>PHP workers</dt><dd>{site?.php_workers || 2}</dd></div>
              <div><dt>WP version</dt><dd>{wpCloud?.data?.wp_version || 'Latest'}</dd></div>
              <div><dt>PHP version</dt><dd>{wpCloud?.data?.php_version || '8.3'}</dd></div>
            </dl>
          </section>
        </aside>
      </div>

      <ConfirmSubscribeModal
        open={confirmOpen}
        onConfirm={confirmSubscribe}
        onClose={() => setConfirmOpen(false)}
        busy={subscribing}
      />
    </>
  );
}
